import React, { useState, useContext, useEffect, useRef } from 'react';
import useSTCP from '../../hooks/useSTCP';
import { STIcon } from 'components/Brand/Icons/STIcon';
import Select from 'components/Core/Select';
import Pagination from 'components/Core/Pagination';
import useBreakpointView from 'utils/useBreakpointView';
import SweetScrollContext from 'utils/SweetScrollContext';
import Modal from 'components/Core/Modal';
import { twMerge } from 'tailwind-merge';
import Button from 'components/Core/Button';
import Tooltip from 'components/Core/Tooltip';

const CardContents = ({ children, stcp, index }) => (
  <>
    <div className="flex flex-col gap-[20px]">
      <h3 className="font-bold mb-0 pr-[15px]">{stcp?.Business_Name}</h3>
      <div className="flex flex-col gap-[12px]">
        <h6 className="font-black text-[var(--grey-6)] mb-0 tracking-wide">STCP CERTIFICATIONS</h6>
        <div className="w-full flex gap-[16px] flex-wrap">
          {stcp?.STCP_Type.map((type, idx) => (
            <div className="flex items-center font-bold text-white text-[14px] bg-[var(--titan-blue-3)] p-[5px] rounded-[4px] !font-['Nunito_Sans']">
              {type}
            </div>
          ))}
        </div>
      </div>
      {stcp?.What_trade_s__do_you_have_experience_knowledge_of_?.length && (
        <div className="flex flex-col gap-[12px]">
          <h6 className="font-black text-[var(--grey-6)] mb-0 tracking-wide">
            INDUSTRIES SERVICED
          </h6>
          <div className="flex w-full flex-wrap gap-[10px]">
            {stcp?.What_trade_s__do_you_have_experience_knowledge_of_?.map((trade, idx) => (
              <Tooltip
                placement="top-end"
                title={trade}
                tooltipStyle={{ maxWidth: 500, fontSize: 'var(--font-size-p)', padding: 25 }}
              >
                <STIcon title={trade} type={trade.toLowerCase().replace(/[ /]/g, '')} />
              </Tooltip>
            ))}
          </div>
        </div>
      )}
    </div>
    {children}
  </>
);

const CardButton = ({ as: Tag = 'button', children, className = '', ...props }) => (
  <Tag
    {...props}
    className={twMerge(
      'no-underline hover:!no-underline cursor-pointer bg-white font-bold text-[var(--darkest)] text-[20px] w-full border border-solid border-[var(--darkest)] rounded-lg flex items-center justify-center p-[var(--button-padding)]',
      className,
    )}
  >
    {children}
  </Tag>
);

const STCPList = ({}) => {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const textRef = useRef(null);
  const { scrollToAnchor } = useContext(SweetScrollContext);
  const isMobile = useBreakpointView(['xs', 'sm']);
  const pageSize = isMobile ? 6 : 9;
  const [filters, setFilters] = useState({
    type: undefined,
    industry: undefined,
  });
  const [page, setPage] = useState(1);
  const [selectedProvider, setSelectedProvider] = useState(null);

  useEffect(() => {
    if (!selectedProvider) return;

    const checkOverflow = () => {
      const el = textRef.current;
      if (el) {
        const hasOverflow = el.scrollHeight > el.clientHeight;
        setIsOverflowing(hasOverflow);
      }
    };

    const timer = setTimeout(checkOverflow, 100);

    return () => clearTimeout(timer);
  }, [selectedProvider]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
    setPage(1);
  };

  const onPage = (page) => {
    setPage(page);
    scrollToAnchor('#stcp-top');
  };

  const stcpData = useSTCP();

  const uniqueAndSorted = (fieldName, arr) => {
    return [...new Set(arr.flatMap((item) => item[fieldName]).filter((x) => x))]
      .sort((a, b) => a.localeCompare(b))
      .map((item) => ({ label: item, value: item }));
  };

  const types = uniqueAndSorted('STCP_Type', stcpData);
  const industries = uniqueAndSorted(
    'What_trade_s__do_you_have_experience_knowledge_of_',
    stcpData,
  );

  const providerDetail = (stcp) => {
    setIsOverflowing(false);
    setExpanded(false);
    setSelectedProvider(stcp);
  };

  const filteredSTCPData = stcpData.filter((item) => {
    const typeMatch = !filters.type || (item?.STCP_Type && item.STCP_Type?.includes(filters.type));
    const industryMatch =
      !filters.industry ||
      item?.What_trade_s__do_you_have_experience_knowledge_of_?.includes(filters.industry);
    return typeMatch && industryMatch;
  });

  const totalPages = Math.ceil(filteredSTCPData.length / pageSize);

  return (
    <div
      id="stcp-top"
      className="bg-[var(--grey-1)] w-full md:m-[var(--margin-center)] [&_*]:font-['Sofia_Pro']"
    >
      <div className="flex flex-col gap-[40px] w-full m-[var(--margin-center)] max-w-[var(--max-width)] p-[var(--mobile-container-padding)] md:p-[var(--container-padding)]">
        <h3 className="font-bold mb-0">Certified Providers</h3>
        <div className="w-full flex flex-col md:flex-row items-center gap-[20px]">
          <Select
            id="type"
            placeholder="STCP Type"
            className="md:!w-[246px]"
            value={filters.type}
            options={[{ label: 'All Types' }, ...types]}
            handleChange={handleFilterChange}
          />
          <Select
            id="industry"
            placeholder="Industry"
            className="md:!w-[246px]"
            value={filters.industry}
            options={[{ label: 'All Industries' }, ...industries]}
            handleChange={handleFilterChange}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-[40px]">
          {filteredSTCPData.slice((page - 1) * pageSize, page * pageSize).map((stcp, index) => (
            <div
              key={index}
              className="flex justify-between flex-col gap-[20px] w-full bg-white min-h-[490px] p-[20px] rounded-lg shadow-md"
            >
              <CardContents stcp={stcp} index={index}>
                <Button
                  button
                  type="transparent - black border"
                  outlined
                  width="100%"
                  onClick={() => providerDetail(stcp)}
                  className="hover:text-[white] hover:bg-[black] hover:[&_svg_path]:fill-[white]"
                >
                  See Details
                </Button>
              </CardContents>
            </div>
          ))}
        </div>
        {filteredSTCPData.length == 0 && (
          <div className="w-full grid-cols-full flex justify-center items-center">
            No results found.
          </div>
        )}
        {totalPages > 1 && (
          <Pagination
            className="sm-max:!pb-[50px] [&_button.button--button]:!rounded-none"
            pageContext={{ currentPage: page, numPages: totalPages }}
            onPage={onPage}
          />
        )}
      </div>
      <Modal
        open={selectedProvider}
        onBackdropClick={() => setSelectedProvider(undefined)}
        handleClose={() => setSelectedProvider(undefined)}
        className="overflow-auto max-h-[90%] max-h-none [&_img.close]:!fixed md:[&_img.close]:!absolute [&_img.close]:!right-[15px] md:[&_img.close]:!right-[20px] [&_.modal-container]:!min-h-fit [&_.modal-container]:!h-full [&_.modal-container]:md:!h-fit [&_.modal-container]:!w-[600px] [&_.modal-container]:!p-[20px]"
      >
        <div className="h-full flex flex-col gap-[30px] [&_*]:font-['Sofia_Pro']">
          <CardContents stcp={selectedProvider} index={0}>
            <div className="flex flex-col gap-[20px] md:h-fit">
              <div className="relative flex flex-col gap-[15px] md:mb-[25px]">
                <h6 className="tracking-wide font-black text-[var(--grey-6)] mb-0">DESCRIPTION</h6>
                <div
                  ref={textRef}
                  className={`relative overflow-hidden md:overflow-y-auto transition-max-height duration-300 flex w-full flex-wrap gap-[10px] h-fit md:pb-[25px] ${
                    expanded ? 'max-h-full' : 'max-h-[200px] md:max-h-[379px]'
                  } whitespace-pre-wrap`}
                >
                  {selectedProvider?.About}
                  {isOverflowing && !expanded && (
                    <div class="md:block pointer-events-none absolute bottom-0 left-0 w-full h-10 bg-gradient-to-t from-white to-transparent"></div>
                  )}
                </div>
                {selectedProvider && isOverflowing && !expanded && (
                  <CardButton
                    onClick={() => setExpanded(true)}
                    className="md:hidden bg-black text-white h-[40px] text-[16px] font-normal flex gap-2"
                  >
                    Expand to read more{' '}
                    <STIcon className="w-[14px] fill-[white]" type="icon-down-caret" />
                  </CardButton>
                )}
              </div>
              <div className="flex flex-row gap-[5px] items-center tracking-wide">
                <h6 className="mb-0 text-[var(--dark)]">CERTIFIED SINCE:</h6>
                {selectedProvider?.Certification_Date}
              </div>
              <div className="flex flex-row gap-[5px] items-center tracking-wide">
                <h6 className="mb-0 text-[var(--dark)]">MAIN CONTACT:</h6>{' '}
                {selectedProvider?.Who_is_the_main_point_of_contact_for_your_company_}
              </div>
            </div>
            <div className="mt-auto w-full flex-col md:flex-row flex mt-[-10px] gap-[20px]">
              {selectedProvider?.Website && (
                <Button
                  button
                  type="transparent - black border"
                  outlined
                  width="50%"
                  to={
                    selectedProvider.Website.startsWith('http')
                      ? selectedProvider.Website
                      : `http://${selectedProvider.Website}`
                  }
                  className="max-h-[62px] hover:text-[white] hover:bg-[black] hover:[&_svg_path]:fill-[white]"
                >
                  <div className="flex items-center gap-[10px]">
                    <div>Website</div>
                    <STIcon type="website" />
                  </div>
                </Button>
              )}
              {selectedProvider?.Phone && (
                <Button
                  to={`tel: ${selectedProvider.Phone}`}
                  type="secondary"
                  className="max-h-[62px]"
                  width="50%"
                >
                  <div className="flex items-center gap-[10px]">
                    <div>Contact Now</div>
                    <STIcon type="phonex" />
                  </div>
                </Button>
              )}
            </div>
          </CardContents>
        </div>
      </Modal>
    </div>
  );
};

export default STCPList;
